import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GlobalDbQueryService } from '@data';
import { Gender, GenderEnum, ProximityUser, ActiveStatus, ProximityRole } from '@models';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { UtilDialogService } from '../../services/dialog.service';

@Component({
  selector: 'prox-user-frm',
  templateUrl: './user-frm.component.html',
  standalone: false,
  host: { class: 'block w-full' },
})
export class UserFrmComponent implements OnInit {
  @Input() proximityUser!: ProximityUser;
  @Input() genders?: Gender[];
  @Input() genderModel?: { genderId: GenderEnum };
  @Input() roles?: ProximityRole[];
  @Input() isBeingAdded!: boolean;
  @Input() isReadOnly!: boolean;
  @Input() autoFocus = true;
  /**
   * When true, the "Change..." link is rendered next to the (disabled) email field on
   * an edit form. The parent is responsible for computing this:
   *   isBeingAdded === false
   *   AND no pending unsaved changes on the form
   *   AND current user is Global Admin or Principal Account Admin.
   */
  @Input() canChangeEmail = false;

  /**
   * Emitted when the email input loses focus (regardless of mode). Optional —
   * parents that don't subscribe see no behavior change. Account-User add-mode
   * uses this to look up whether the email already belongs to a user in the
   * current account, so it can offer to attach to the existing record rather
   * than create a duplicate.
   */
  @Output() emailBlur = new EventEmitter<string | null | undefined>();

  onEmailBlur() {
    this.emailBlur.emit(this.proximityUser?.email);
  }

  /**
   * Emitted after the existing First-Name blur handling runs. Optional — parents
   * use this to refresh first-name-dependent UI (e.g. the Account User add-mode
   * Helpful Hints panel that personalizes its tips with the user's first name).
   */
  @Output() firstNameBlur = new EventEmitter<string | null | undefined>();

  async onFirstNameBlur() {
    await this.updateUserNameDeps();
    this.firstNameBlur.emit(this.proximityUser?.firstName);
  }

  isPageReady = false;
  activeStatuses: ActiveStatus[] = [];

  constructor(
    public dbQueryService: GlobalDbQueryService,
    private authService: AuthService,
    private dialogService: UtilDialogService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.isPageReady = true;
  }

  async updateUserNameDeps() {
    if (!this.isBeingAdded) return;
    if (this.proximityUser.firstName == null || this.proximityUser.firstName.length == 0) return;
    if (this.proximityUser.lastName == null || this.proximityUser.lastName.length == 0) return;

    const baseName = this.proximityUser.firstName.substring(0, 1) + this.proximityUser.lastName;
    this.proximityUser.username = baseName;
    await this.findUniqueUserName(baseName);
  }

  /**
   * Auto-suggest a username derived from First+Last, suffixing a sequenced integer if the
   * base name collides. Authoritative source is AuthService.isUsernameAvailable — which hits
   * the server's UserManager.FindByNameAsync, catching even orphan AspNetUsers rows that
   * have no matching ProximityUser (which a client-side Breeze query against ProximityUsers
   * cannot see). Bounded loop to keep this defensive against any future endpoint failure.
   */
  async findUniqueUserName(baseName: string) {
    const MaxAttempts = 50;
    let candidate = baseName;
    for (let ix = 0; ix < MaxAttempts; ix++) {
      const ok = await this.authService.isUsernameAvailable(candidate);
      if (ok) {
        this.proximityUser.username = candidate;
        return;
      }
      candidate = baseName + (ix + 1).toString();
    }
    // Highly unlikely to land here — would mean 50 sequenced suffixes are all taken AND the
    // server kept answering "not available." Leave the username as-is; beforeSave will refuse
    // to proceed (and the save-time error path remains as a final backstop).
    this.proximityUser.username = candidate;
  }

  /**
   * Change-Email flow, opened from the inline "Change..." link beside the (disabled)
   * email field on an edit form. Prompts for the new email, then routes:
   *   - free      → server SetEmail, mirror locally as committed, toast success.
   *   - collision → open the conflict dialog (orphan/inactive → reassign+set;
   *                                            active → reassign disabled).
   *   - cancelled at any prompt → no-op, no changes.
   *
   * In both server-side success paths the write has already committed atomically on the
   * server, so we mirror the new email into the local Breeze entity AS IF IT HAD ALWAYS
   * BEEN THERE — using setUnchanged() so the form does not register a pending change.
   * The Save button must not light up after this flow; the change is already saved.
   */
  async onChangeEmailClick() {
    const currentDisplay = this.proximityUser.email || this.proximityUser.username || '';
    const newEmail = await this.dialogService.changeEmailPromptDialog(currentDisplay);
    if (!newEmail) return;

    // No-op if the user typed the same email that's already on file.
    if ((this.proximityUser.email ?? '').trim().toLowerCase() === newEmail.toLowerCase()) {
      return;
    }

    const conflictRes = await this.authService.getEmailConflict(newEmail, this.proximityUser.id);
    if (conflictRes.available) {
      const setRes = await this.authService.changeEmail(this.proximityUser.id, newEmail);
      if (!setRes.success) {
        this.toastr.error(setRes.message ?? 'Could not change the email.');
        return;
      }
      await this.commitEmailLocally(newEmail);
      this.toastr.success('Email updated.');
      return;
    }

    // Collision: open the conflict dialog. On reassign the server has already moved the
    // email onto this user — mirror it locally as committed and toast. On cancel, the
    // original email stays on file untouched.
    if (!conflictRes.conflict) return;
    const outcome = await this.dialogService.emailConflictDialog(conflictRes.conflict, this.proximityUser.id);
    if (outcome === 'reassigned') {
      await this.commitEmailLocally(newEmail);
      this.toastr.success('Email reassigned.');
    }
  }

  /**
   * Mirror a server-side email write into the local Breeze entity without dirtying it.
   * Re-fetches the ProximityUser from the server so the in-cache entity picks up BOTH the
   * new email AND the new RowVersion that the server bumped during its SaveChanges. A plain
   * setUnchanged() leaves the stale rowVersion in the entity's value bag, which then trips
   * a DbUpdateConcurrencyException on the next normal save of an unrelated field (e.g.
   * editing mobile after Change Email...). fetchEntityByKey merges fresh server values
   * over the cached entity and resets EntityState to Unchanged, so the form's hasChanges()
   * stays false. Parameter kept for signature stability — the refreshed entity carries the
   * authoritative email.
   */
  private async commitEmailLocally(_newEmail: string) {
    await this.proximityUser.entityAspect.entityManager!.fetchEntityByKey(
      this.proximityUser.entityAspect.getKey(),
    );
  }
}
